import React, { useCallback } from 'react'

import { useMisconfiguredDoors } from '../hooks/useMisconfiguredDoors'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'

import { AddIcon } from '@chakra-ui/icons'
import { Box, Text, useDisclosure } from '@chakra-ui/react'

import { Button } from '@/components/ui'
import {
  useDefaultAiOperatorModelQuery,
  useEnabledAiOperatorModelsQuery,
} from '@/graphql/generated/hooks'
import { AiOperatorActionType } from '@/graphql/generated/schemas'
import { uuid } from '@/utils/helpers'

import { Criterion, CriterionOption } from '../types/types'
import {
  DEFAULT_MODEL_ID,
  containsNoUnauthorizedEntry,
  getAiOperatorModels,
  getAvailableCriteriaForItem,
  getDefaultActionForCriterion,
  getDefaultAiOperatorModel,
} from '../utils/utils'
import { AiOperatorCriterionItem } from './AiOperatorCriterionItem'
import { AiOperatorNoCriteriaBanner } from './AiOperatorNoCriteriaBanner'
import { DefaultModelAlert } from './DefaultModelAlert'
import { DeviceMisconfiguredAlert } from './DeviceMisconfiguredAlert'
import { MisconfiguredDoorsModals } from './MisconfiguredDoorsModals'

interface AiOperatorCriteriaIProps {
  onChange: (
    criteria: Criterion[] | ((prev: Criterion[]) => Criterion[])
  ) => void
  criteria: Criterion[]
  criteriaOptions: Criterion[]
  disabled?: boolean
  /**
   * Doors to run the always-on misconfiguration warning against (legacy form).
   * Omit to disable the internal check entirely — the v2 form runs its own
   * on-demand DoorConfigurationCheck instead.
   */
  doorIds?: string[]
  isLoading?: boolean
}

const AiOperatorCriteria = ({
  criteria,
  onChange,
  doorIds,
  disabled = false,
  criteriaOptions: availableCriteria,
  isLoading,
}: AiOperatorCriteriaIProps) => {
  const { t } = useTranslation()
  const mode = useLightMode()
  const { isImplementer } = usePermissions()
  const { onOpen, isOpen, onClose } = useDisclosure()
  const { misconfiguredDoors, refetchDoors } = useMisconfiguredDoors({
    doorIds: doorIds ?? [],
  })
  const { data: aiOperatorModelsData } = useEnabledAiOperatorModelsQuery({
    skip: !isImplementer,
  })
  const { data: defaultAiOperatorModelData } = useDefaultAiOperatorModelQuery({
    skip: !isImplementer,
  })

  const defaultAiOperatorModel = getDefaultAiOperatorModel(
    defaultAiOperatorModelData
  )
  const aiOperatorModels = getAiOperatorModels(aiOperatorModelsData)

  // region callbacks
  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return

      onChange((prevCriteria) => {
        const items = Array.from(prevCriteria)
        const [reorderedItem] = items.splice(result.source.index, 1)
        items.splice(result.destination.index, 0, reorderedItem)
        return items
      })
    },
    [onChange]
  )

  const handleDelete = useCallback(
    (id: string) => {
      onChange((prevCriteria) => prevCriteria.filter((item) => item.id !== id))
    },
    [onChange]
  )

  const handleAddCriterion = useCallback(() => {
    if (availableCriteria.length === 0) return

    onChange((prevCriteria) => {
      const usedValues = new Set(prevCriteria.map((c) => c.criterion.id))
      const firstUnused = availableCriteria.find(
        (opt) => !usedValues.has(opt.id)
      )

      // don't add
      if (!firstUnused) return prevCriteria

      const newCriterion: Criterion = {
        id: uuid(),
        action: getDefaultActionForCriterion(firstUnused.criterion.description),
        criterion: firstUnused.criterion,
        detected: true,
        // undefined = use current default model (dynamic, not pinned)
        aiOperatorModelId: undefined,
      }

      return [...prevCriteria, newCriterion]
    })
  }, [availableCriteria, onChange])

  const handleUpdateCriterion = useCallback(
    (id: string, newCriterion: CriterionOption) => {
      onChange((prevCriteria) =>
        prevCriteria.map((item) =>
          item.id === id
            ? {
                ...item,
                criterion: newCriterion,
              }
            : item
        )
      )
    },
    [onChange]
  )

  const handleUpdateCriterionModel = useCallback(
    (id: string, modelId: string | undefined) => {
      // map DEFAULT_MODEL_ID to undefined to indicate using backend default
      const aiOperatorModelId =
        modelId === DEFAULT_MODEL_ID ? undefined : modelId

      onChange((prevCriteria) =>
        prevCriteria.map((item) =>
          item.id === id
            ? {
                ...item,
                aiOperatorModelId,
              }
            : item
        )
      )
    },
    [onChange]
  )

  const handleUpdateAction = useCallback(
    (id: string, newAction: AiOperatorActionType) => {
      onChange((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, action: newAction } : item
        )
      )
    },
    [onChange]
  )
  // endregion

  const numberOfMisconfiguredDoors = misconfiguredDoors.length
  const hasAuthorizedEgressCriteria = containsNoUnauthorizedEntry(criteria)
  const shouldShowMisconfigurationAlert =
    numberOfMisconfiguredDoors > 0 && hasAuthorizedEgressCriteria

  const hasNoCriterion = criteria.length === 0 && !isLoading

  const usedCriteriaValues = new Set(criteria.map((c) => c.criterion.id))
  const hasUnusedOptions = availableCriteria.some(
    (opt) => !usedCriteriaValues.has(opt.id)
  )

  return (
    <Box mb={6} width='100%'>
      {shouldShowMisconfigurationAlert && (
        <DeviceMisconfiguredAlert
          count={numberOfMisconfiguredDoors}
          onClick={onOpen}
        />
      )}
      <DefaultModelAlert
        criteria={criteria}
        defaultAiOperatorModel={defaultAiOperatorModel}
        isImplementer={isImplementer}
      />
      <Text
        color={`text.primary.${mode}`}
        fontSize='16px'
        fontWeight='medium'
        letterSpacing='-0.8px'
        mb={2}
      >
        {t('automations:ai_criteria_title')}
      </Text>
      <Text color={`text.secondary.${mode}`} fontSize='sm' mb={4}>
        {t('automations:ai_criteria_drag_hint')}
      </Text>

      {hasNoCriterion && (
        <AiOperatorNoCriteriaBanner
          hasNoCriterion={hasNoCriterion}
          message={t('automations:ai_criteria_no_criteria_message')}
        />
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId='criteria-list'>
          {(provided) => (
            <Box
              {...provided.droppableProps}
              mb={4}
              ref={provided.innerRef}
              width='100%'
            >
              {criteria.map((item, index) => {
                const itemAvailableCriteria = getAvailableCriteriaForItem(
                  item,
                  criteria,
                  availableCriteria
                )

                return (
                  <Draggable draggableId={item.id} index={index} key={item.id}>
                    {(provided) => (
                      <AiOperatorCriterionItem
                        aiOperatorModels={aiOperatorModels}
                        availableCriteria={itemAvailableCriteria}
                        disabled={disabled}
                        handleDelete={handleDelete}
                        handleUpdateAction={handleUpdateAction}
                        handleUpdateCriterion={handleUpdateCriterion}
                        isImplementer={isImplementer}
                        item={item}
                        onAiOperatorModelChange={handleUpdateCriterionModel}
                        provided={provided}
                      />
                    )}
                  </Draggable>
                )
              })}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      {!disabled && (
        <Button
          borderColor={`border.strong.${mode}`}
          fontSize='12px'
          h='30px'
          isDisabled={!hasUnusedOptions}
          leftIcon={<AddIcon />}
          onClick={handleAddCriterion}
          px='10px'
          rounded='0px'
          variant='v2-ghost'
        >
          {t('automations:ai_criteria_add_button')}
        </Button>
      )}
      <MisconfiguredDoorsModals
        devices={misconfiguredDoors}
        isOpen={isOpen}
        onClose={onClose}
        onDoorSaved={refetchDoors}
      />
    </Box>
  )
}

export { AiOperatorCriteria }
